import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useWebSocketContext } from '../contexts/WebSocketContext';

interface ConnectionStatusProps {
  showLabel?: boolean;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  showLabel = true,
  showRefreshButton = true,
  onRefresh
}) => {
  const { isConnected, connectionStatus, error, reconnectAttempts, connect } = useWebSocketContext();

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'warning';
      case 'disconnected':
        return 'default';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <WifiIcon />;
      case 'connecting':
        return <CircularProgress size={16} />;
      case 'disconnected':
        return <WifiOffIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <WifiOffIcon />;
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return 'Disconnected';
      case 'error':
        return 'Connection Error';
      default:
        return 'Unknown';
    }
  };

  const getTooltipText = () => {
    let text = getStatusText();
    
    if (connectionStatus === 'error' && error) {
      text += `: ${error}`;
    }
    
    if (reconnectAttempts > 0) {
      text += ` (Reconnect attempt ${reconnectAttempts})`;
    }
    
    return text;
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      connect();
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title={getTooltipText()}>
        <Chip
          icon={getStatusIcon()}
          label={showLabel ? getStatusText() : ''}
          color={getStatusColor() as any}
          size="small"
          variant={isConnected ? 'filled' : 'outlined'}
        />
      </Tooltip>
      
      {showRefreshButton && connectionStatus !== 'connected' && (
        <Tooltip title="Reconnect">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={connectionStatus === 'connecting'}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default ConnectionStatus;
